import { useState, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useSequenceUpload } from '@/hooks/useSequenceUpload';
import { MigrationStatus } from '@/hooks/useMigrationProgress';
import { supabase } from '@/integrations/supabase/client';
import MigrationLayoutPhotoUpload from './MigrationLayoutPhotoUpload';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle2, XCircle, Clock, Upload, SkipForward } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface SequenceInfo {
  id: string;
  title: string;
  storage_path: string;
  download_count: number;
  user_id: string;
  created_at: string;
}

interface SequenceUploadCardProps {
  sequence: SequenceInfo;
  status?: MigrationStatus;
  onUploadComplete?: () => void;
}

export function SequenceUploadCard({ sequence, status, onUploadComplete }: SequenceUploadCardProps) {
  const { uploadSequenceFile, markAsSkipped, uploadStatus } = useSequenceUpload();
  const [isDragging, setIsDragging] = useState(false);
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const [skipNotes, setSkipNotes] = useState('');

  const currentStatus = uploadStatus[sequence.id]?.status || 'idle';
  const uploadProgress = uploadStatus[sequence.id]?.progress || 0;

  // Get the final status (from migration_progress if available, otherwise use upload status)
  const finalStatus: MigrationStatus = status || 'pending';
  const isComplete = finalStatus === 'complete';
  const isSkipped = finalStatus === 'skipped';
  const isPending = finalStatus === 'pending';
  const isUploading = currentStatus === 'uploading';

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!isComplete && !isSkipped && !isUploading) {
      setIsDragging(true);
    }
  }, [isComplete, isSkipped, isUploading]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (isComplete || isSkipped || isUploading) {
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    const file = files[0]; // Only take the first file

    // Validate file is a .zip or sequence file
    const allowedExtensions = ['zip', 'fseq', 'lms', 'seq', 'xml', 'piz', 'loredit'];
    const ext = (file.name.split('.').pop() || '').toLowerCase();

    if (!allowedExtensions.includes(ext)) {
      alert(`Invalid file type. Allowed: ${allowedExtensions.join(', ')}`);
      return;
    }

    // Get current user ID from session instead of using sequence.user_id from CSV
    const { data: session } = await supabase.auth.getSession();
    const currentUserId = session.session?.user?.id;
    
    if (!currentUserId) {
      console.error('No authenticated user found');
      return;
    }
    
    // Start upload
    const result = await uploadSequenceFile(
      file,
      sequence.id,
      currentUserId, // Use current user's ID instead of sequence.user_id
      sequence.storage_path
    );

    if (result.success && onUploadComplete) {
      onUploadComplete();
    }
  }, [sequence, uploadSequenceFile, isComplete, isSkipped, isUploading, onUploadComplete]);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    
    // Get current user ID from session instead of using sequence.user_id from CSV
    const { data: session } = await supabase.auth.getSession();
    const currentUserId = session.session?.user?.id;
    
    if (!currentUserId) {
      console.error('No authenticated user found');
      return;
    }
    
    const result = await uploadSequenceFile(
      file,
      sequence.id,
      currentUserId, // Use current user's ID instead of sequence.user_id
      sequence.storage_path
    );

    if (result.success && onUploadComplete) {
      onUploadComplete();
    }

    // Reset input
    e.target.value = '';
  }, [sequence, uploadSequenceFile, onUploadComplete]);

  const handleSkip = async () => {
    const result = await markAsSkipped(sequence.id, skipNotes || undefined);
    if (result.success) {
      setSkipDialogOpen(false);
      setSkipNotes('');
      if (onUploadComplete) {
        onUploadComplete();
      }
    }
  };

  const getStatusBadge = () => {
    if (isUploading) {
      return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Uploading...</Badge>;
    }
    if (isComplete) {
      return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200"><CheckCircle2 className="w-3 h-3 mr-1" />Complete</Badge>;
    }
    if (isSkipped) {
      return <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200"><SkipForward className="w-3 h-3 mr-1" />Skipped</Badge>;
    }
    return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
  };

  // Format created date with fallback for invalid dates
  const getFormattedDate = () => {
    try {
      if (!sequence.created_at) return 'Date unknown';
      const date = new Date(sequence.created_at);
      if (isNaN(date.getTime())) return 'Date unknown';
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      return 'Date unknown';
    }
  };

  return (
    <Card className={`transition-all ${isDragging ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">{sequence.title}</CardTitle>
            <CardDescription className="mt-1">
              {sequence.download_count} downloads •
              Created {getFormattedDate()}
            </CardDescription>
          </div>
          {getStatusBadge()}
        </div>
      </CardHeader>

      <CardContent>
        {isUploading && (
          <div className="space-y-2 mb-4">
            <Progress value={uploadProgress} className="h-2" />
            <p className="text-sm text-muted-foreground text-center">{uploadProgress}%</p>
          </div>
        )}

        {!isComplete && !isSkipped && !isUploading && (
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`
              border-2 border-dashed rounded-lg p-8 text-center transition-colors
              ${isDragging
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400 bg-gray-50'
              }
            `}
          >
            <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-sm font-medium text-gray-700 mb-2">
              Drag & drop sequence file here
            </p>
            <p className="text-xs text-gray-500 mb-4">
              Supported: .zip, .fseq, .lms, .seq, .xml, .piz, .loredit
            </p>
            <label htmlFor={`file-input-${sequence.id}`}>
              <Button variant="outline" size="sm" asChild>
                <span>
                  Browse Files
                </span>
              </Button>
              <input
                id={`file-input-${sequence.id}`}
                type="file"
                className="hidden"
                accept=".zip,.fseq,.lms,.seq,.xml,.piz,.loredit"
                onChange={handleFileSelect}
              />
            </label>
          </div>
        )}

        {isComplete && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-green-600 mb-2" />
            <p className="text-sm font-medium text-green-800">Migration Complete</p>
            <p className="text-xs text-green-600 mt-1">File uploaded and database updated</p>
          </div>
        )}

        {isSkipped && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
            <SkipForward className="mx-auto h-12 w-12 text-gray-600 mb-2" />
            <p className="text-sm font-medium text-gray-800">Skipped</p>
            <p className="text-xs text-gray-600 mt-1">This sequence was skipped</p>
          </div>
        )}

        {/* Layout Photos Section - Show for all statuses except uploading */}
        {!isUploading && (
          <div className="mt-4">
            <MigrationLayoutPhotoUpload
              sequenceId={sequence.id}
              maxPhotos={3}
              disabled={isComplete || isSkipped}
            />
          </div>
        )}

        <div className="mt-4 text-xs text-gray-500 space-y-1">
          <p><span className="font-medium">ID:</span> {sequence.id}</p>
          <p><span className="font-medium">Current Path:</span> {sequence.storage_path}</p>
        </div>
      </CardContent>

      {!isComplete && !isSkipped && !isUploading && (
        <CardFooter>
          <Dialog open={skipDialogOpen} onOpenChange={setSkipDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="ghost" size="sm" className="w-full">
                <XCircle className="w-4 h-4 mr-2" />
                Skip This Sequence
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Skip Sequence</DialogTitle>
                <DialogDescription>
                  Mark "{sequence.title}" as skipped. You can optionally add notes about why it's being skipped.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="skip-notes">Notes (Optional)</Label>
                  <Textarea
                    id="skip-notes"
                    placeholder="e.g., File not found in Facebook group, Duplicate sequence, etc."
                    value={skipNotes}
                    onChange={(e) => setSkipNotes(e.target.value)}
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSkipDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSkip}>
                  Mark as Skipped
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardFooter>
      )}
    </Card>
  );
}
